/**
 * Board params for planned-work items. One entity serves every parent — Service Line, Service Line
 * Task and both template twins read standing lines; a Visit reads the effective lines its exceptions
 * resolve to — so the params carry the parent pointer rather than a per-entity host record.
 */
import { ActivityEntityName, PlannedItemParent, VisitBrowseRow } from '@jms/model';
import type { ItemBrowserAddAdapter } from '@workbuddy/entities/shared/item.browser.dialog';
import type { EmbeddedItemHostParams } from '@workbuddy/surfaces/embedded-item';
import type { EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board';

/** Empty: this board queries the item entity, so the row is already the line. */
export const PLANNED_ITEM_SCOPE = '';

export const PLANNED_ITEM_QUERY_KEY = [ActivityEntityName.PlannedItem, 'items'];

/**
 * A Visit's board wants its rows bucketed by `origin.name` — the Line's defaults, then each due
 * task's items, then this visit's own additions. Grouping is a property of the VIEW, not of a mount:
 * the board reads `table.groupBy` off the server view document (layered with the user's own display
 * settings), and neither this context nor the manifest board defaults can seed it. The Visit's server
 * view carries that field; nothing here applies it.
 */
export interface PlannedItemBoardParams extends EmbeddedItemHostParams {
  /** Parent-specific wording when the catalogue is opened before the parent exists. */
  readonly requiresParentMessage?: string;
  /** The owner the board reads and writes standing lines against. Null before the host record loads. */
  readonly parent: () => PlannedItemParent | null;
  /**
   * Present only on a visit-scoped mount: writes become exceptions scoped to this attendance, and the
   * rows carry Exclude / Restore / Remove instead of a Delete.
   *
   * Typed to the identity every consumer here actually reads. The Visit's own board passes its whole
   * `VisitBrowseRow`, which satisfies this; a Visit-scoped task editor has only the id its scoped
   * record carries, and nothing on this board needs more than that.
   */
  readonly visit?: () => Pick<VisitBrowseRow, '_id'> | null;
  readonly writable: () => boolean;
  readonly itemBrowser?: () => ItemBrowserAddAdapter;
}

export type PlannedItemBoardContext = EntityBoardContext & { readonly params: PlannedItemBoardParams };
